import React from 'react';
import styled from 'styled-components';
import { FaDownload } from 'react-icons/fa';
import { personalInfo, heroStats } from '../data/personal';
import { Button } from '../components/common';

const HeroContainer = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.background} 0%, #1a1a2e 50%, ${({ theme }) => theme.colors.background} 100%);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-height: 100svh; /* Use svh for better mobile viewport handling */
    padding-top: 80px; /* Account for fixed header */
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  text-align: center;
  z-index: 2;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0 ${({ theme }) => theme.spacing.lg}; /* Increased mobile padding */
  }
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.1;
  background: radial-gradient(circle at 20% 80%, ${({ theme }) => theme.colors.navyBlue} 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, ${({ theme }) => theme.colors.lightNavy} 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, ${({ theme }) => theme.colors.charcoal} 0%, transparent 50%);
  animation: gentle-float 8s ease-in-out infinite;
  will-change: transform;
`;

const Name = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['6xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.textPrimary}, ${({ theme }) => theme.colors.navyBlue});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
    margin-bottom: ${({ theme }) => theme.spacing.lg}; /* Increased mobile spacing */
    line-height: 1.2; /* Better mobile line height */
  }
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.navyBlue};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    margin-bottom: ${({ theme }) => theme.spacing.xl}; /* Increased mobile spacing */
  }
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto ${({ theme }) => theme.spacing['2xl']};
  line-height: 1.8;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fontSizes.base};
    margin-bottom: ${({ theme }) => theme.spacing['3xl']}; /* Increased mobile spacing */
    line-height: 1.7; /* Optimized mobile line height */
    max-width: 100%; /* Full width on mobile */
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing['4xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.lg}; /* Increased mobile gap */
    margin-bottom: ${({ theme }) => theme.spacing['3xl']};
    
    /* Make buttons full width on mobile for better touch targets */
    & > * {
      width: 100%;
      max-width: 280px; /* Reasonable max width */
      min-height: 48px; /* Minimum touch target size */
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing['4xl']};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr); /* Force 2 columns on mobile */
    gap: ${({ theme }) => theme.spacing.lg};
    margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  }
`;

const Stat = styled.div`
  text-align: center;
  
  .stat-number {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.navyBlue};
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes['2xl']}; /* Smaller on mobile */
    }
  }
  
  .stat-label {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 1px;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      font-size: ${({ theme }) => theme.fontSizes.xs}; /* Smaller on mobile */
      letter-spacing: 0.5px;
    }
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

const FloatingElement = styled.div<{
  size: number;
  x: number;
  y: number;
  duration: number;
  delay: number;
}>`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  background: radial-gradient(circle, rgba(30, 58, 138, 0.1), transparent);
  border-radius: 50%;
  left: ${({ x }) => x}%;
  top: ${({ y }) => y}%;
  animation: gentle-float ${({ duration }) => duration}s ease-in-out infinite ${({ delay }) => delay}s;
  will-change: transform;
`;

const floatingElements = Array.from({ length: 6 }, (_, i) => ({
  id: i,
  size: Math.random() * 100 + 50,
  x: Math.random() * 100,
  y: Math.random() * 100,
  duration: Math.random() * 10 + 15,
  delay: Math.random() * 5,
}));

export const HeroSection: React.FC = () => {
  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = '/Jeremiah_Manuel_Resume.pdf';
    link.download = 'Jeremiah_Manuel_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <HeroContainer id="home">
      <HeroBackground aria-hidden="true" />
      <FloatingElements aria-hidden="true">
        {floatingElements.map((element) => (
          <FloatingElement
            key={element.id}
            size={element.size}
            x={element.x}
            y={element.y}
            duration={element.duration}
            delay={element.delay}
          />
        ))}
      </FloatingElements>
      
      <HeroContent className="fade-in-up">
        <header>
          <Name as="h1">{personalInfo.name}</Name>
          <Title as="h2">{personalInfo.title}</Title>
          <Subtitle>{personalInfo.subtitle}</Subtitle>
        </header>
        
        <ButtonGroup role="group" aria-label="Primary actions">
          <Button 
            variant="primary" 
            size="lg" 
            onClick={scrollToContact}
            aria-label="Navigate to contact section to get in touch"
          >
            Get In Touch
          </Button>
          <Button 
            variant="secondary" 
            size="lg" 
            onClick={handleDownloadResume}
            aria-label="Download Jeremiah's resume PDF"
          >
            {React.createElement(FaDownload as any, { 
              style: { marginRight: '8px' },
              'aria-hidden': true 
            })}
            Download Resume
          </Button>
        </ButtonGroup>

        <section aria-labelledby="hero-stats-heading">
          <h3 id="hero-stats-heading" className="sr-only">Professional Statistics</h3>
          <StatsContainer role="list" aria-label="Professional achievements and experience">
            <Stat role="listitem">
              <span className="stat-number" aria-label="4 plus years">{heroStats.experience}</span>
              <span className="stat-label">Years Experience</span>
            </Stat>
            <Stat role="listitem">
              <span className="stat-number" aria-label="10 plus projects">{heroStats.projectsCompleted}</span>
              <span className="stat-label">Projects Completed</span>
            </Stat>
            <Stat role="listitem">
              <span className="stat-number" aria-label="15 plus technologies">{heroStats.technologies}</span>
              <span className="stat-label">Technologies</span>
            </Stat>
            <Stat role="listitem">
              <span className="stat-number" aria-label="Infinite coffee consumed">{heroStats.coffeeConsumed}</span>
              <span className="stat-label">Coffee Consumed</span>
            </Stat>
          </StatsContainer>
        </section>
      </HeroContent>
    </HeroContainer>
  );
}; 